import React, { useCallback, useEffect, useState } from "react";
import { Table, Input, Select, Space, Tag, Tooltip, Avatar, Badge, Button, Modal, message } from "antd";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faRotate, faFileCsv, faBell } from "@fortawesome/free-solid-svg-icons";
import rewardProgramApi from "../../../api/rewardProgramApi";
import RewardUserOrdersTable from "./reward-user-orders-table";
import {
    orderStatusMeta, rewardAmountTitle, PARTICIPANT_STATUS_META, fmtMoney, fmtNumber, fmtUnix,
} from "./reward-shared-meta";

const FILTERS = [
    { value: "all", label: "Tất cả người đăng ký" },
    { value: "no_order", label: "Đăng ký nhưng CHƯA có đơn" },
    { value: "has_order", label: "Đã có đơn trong đợt" },
    { value: "rewarded", label: "Đã có tiền thưởng" },
];
const SORTS = [
    { value: "joinTime", label: "Đăng ký mới nhất" },
    { value: "joinTimeAsc", label: "Đăng ký sớm nhất" },
    { value: "reward", label: "Thưởng cao nhất" },
    { value: "progress", label: "Tiến độ cao nhất" },
];

/**
 * Tab Người tham gia: ai đăng ký đợt, đăng ký lúc nào, đã phát sinh bao nhiêu
 * đơn / tiền. Lọc + phân trang chạy ở server (đợt có thể vài chục nghìn người).
 */
function RewardParticipantsTab({ program }) {
    const programId = program.id;
    const navigate = useNavigate();
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [pushLoading, setPushLoading] = useState(false);
    const [query, setQuery] = useState({ search: "", filter: "all", sort: "joinTime", page: 1, pageSize: 20 });

    const fetchRows = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await rewardProgramApi.participants(programId, query);
            setRows(data.rows || []);
            setTotal(data.total || 0);
        } catch {
            message.error("Không tải được danh sách người tham gia");
        } finally {
            setLoading(false);
        }
    }, [programId, query]);

    useEffect(() => { fetchRows(); }, [fetchRows]);

    const patch = (next) => setQuery((q) => ({ ...q, page: 1, ...next }));

    // Export lấy TOÀN BỘ tập đang lọc từ server (không chỉ trang đang xem).
    const exportCsv = async () => {
        setExporting(true);
        try {
            const { data } = await rewardProgramApi.participantsCsv(programId, { ...query, page: undefined, pageSize: undefined });
            const a = document.createElement("a");
            a.href = URL.createObjectURL(data);
            a.download = `nguoi-tham-gia-${program.slug}-${query.filter}.csv`;
            a.click();
            URL.revokeObjectURL(a.href);
        } catch {
            message.error("Export thất bại");
        } finally {
            setExporting(false);
        }
    };

    /**
     * Nạp userId của tập đang lọc sang form Push (mode 'ids') — KHÔNG gửi thẳng
     * từ đây: admin vẫn phải xem preview số người, sửa nội dung, chọn gửi ngay
     * hay hẹn giờ ở màn Push như mọi chiến dịch khác.
     */
    const sendPush = async () => {
        setPushLoading(true);
        try {
            const { data } = await rewardProgramApi.participantUserIds(programId, { search: query.search, filter: query.filter });
            if (!data.userIds?.length) return message.warning("Không có ai khớp bộ lọc");
            Modal.confirm({
                title: `Soạn push cho ${fmtNumber(data.userIds.length)} người?`,
                content: data.capped
                    ? `Tập lọc có ${fmtNumber(data.total)} người, vượt trần ${fmtNumber(data.max)} — chỉ nạp ${fmtNumber(data.userIds.length)} người đăng ký mới nhất.`
                    : "Danh sách sẽ được nạp sẵn vào form Push để xem trước và sửa nội dung trước khi gửi.",
                okText: "Soạn push", cancelText: "Huỷ",
                onOk: () =>
                    navigate("/push/new", {
                        state: {
                            audienceUserIds: data.userIds,
                            preset: {
                                title: program.name,
                                message:
                                    query.filter === "no_order"
                                        ? `Bạn đã đăng ký "${program.name}" nhưng chưa có đơn nào. Mua ngay để nhận thưởng!`
                                        : `Cập nhật mới từ "${program.name}".`,
                                path: `/chuong-trinh-thuong/${program.slug}`,
                            },
                        },
                    }),
            });
        } catch {
            message.error("Không lấy được danh sách userId");
        } finally {
            setPushLoading(false);
        }
    };

    const columns = [
        {
            title: "Người dùng", width: 250, fixed: "left",
            render: (_, r) => (
                <Space>
                    <Avatar size={32} src={r.avatar || undefined}>{(r.name || "U").charAt(0).toUpperCase()}</Avatar>
                    <div className="leading-tight">
                        <div className="font-medium">
                            {r.name || `User ${r.userId}`}
                            {r.isBuffUser ? <Tag color="volcano" className="ml-1">BUFF</Tag> : null}
                            {r.userActive === false ? <Tag className="ml-1">Đã khoá</Tag> : null}
                        </div>
                        <div className="text-xs text-content-3">#{r.userId} · {r.email || "—"}</div>
                    </div>
                </Space>
            ),
        },
        {
            title: "Đăng ký lúc", dataIndex: "joinTime", width: 155,
            render: (v, r) => (
                <div className="leading-tight">
                    <div>{fmtUnix(v)}</div>
                    <div className="text-xs text-content-3">TK tạo {fmtUnix(r.userCreateTime, "DD/MM/YYYY")}</div>
                </div>
            ),
        },
        {
            title: "Đơn trong đợt", width: 230,
            render: (_, r) => {
                if (!r.totalOrders) return <span className="text-orange-500">Chưa có đơn nào</span>;
                return (
                    <Space size={4} wrap>
                        {Object.entries(orderStatusMeta(program)).map(([key, meta]) => {
                            const stat = r.orderStats?.[key];
                            if (!stat?.count) return null;
                            return (
                                <Tooltip key={key} title={`${meta.label}: ${fmtMoney(stat.amount)}`}>
                                    <Tag color={meta.color} style={{ marginInlineEnd: 0 }}>{meta.label} {stat.count}</Tag>
                                </Tooltip>
                            );
                        })}
                    </Space>
                );
            },
        },
        {
            title: "Mua gần nhất", dataIndex: "lastPurchaseTime", width: 140,
            render: (v) => (v ? fmtUnix(v, "DD/MM HH:mm") : "—"),
        },
        {
            title: rewardAmountTitle(program), dataIndex: "rewardTotal", width: 110, align: "right",
            render: (v) => <b className={Number(v) ? "text-green-600" : "text-content-3"}>{fmtMoney(v)}</b>,
        },
        {
            title: "Trạng thái", dataIndex: "status", width: 120,
            render: (v) => <Tag color={PARTICIPANT_STATUS_META[v]?.color}>{PARTICIPANT_STATUS_META[v]?.label || v}</Tag>,
        },
    ];

    return (
        <div className="flex flex-col gap-3">
            <Space wrap>
                <Input.Search
                    allowClear placeholder="Tìm theo userId / tên / email" style={{ width: 280 }}
                    onSearch={(v) => patch({ search: v })}
                />
                <Select value={query.filter} onChange={(v) => patch({ filter: v })} options={FILTERS} style={{ width: 230 }} />
                <Select value={query.sort} onChange={(v) => patch({ sort: v })} options={SORTS} style={{ width: 190 }} />
                <Button icon={<FontAwesomeIcon icon={faRotate} />} onClick={fetchRows}>Tải lại</Button>
                <Button icon={<FontAwesomeIcon icon={faFileCsv} />} loading={exporting} onClick={exportCsv}>Export CSV</Button>
                <Tooltip title="Nạp danh sách đang lọc sang form Push để soạn nội dung nhắc mua">
                    <Button icon={<FontAwesomeIcon icon={faBell} />} loading={pushLoading} onClick={sendPush}>Gửi push nhắc</Button>
                </Tooltip>
                <Badge count={fmtNumber(total)} showZero color="#2563eb" overflowCount={Infinity} />
                <span className="text-content-3 text-sm">người khớp bộ lọc</span>
            </Space>
            <Table
                size="small" rowKey="id" dataSource={rows} columns={columns} loading={loading}
                scroll={{ x: "max-content" }}
                expandable={{
                    // Chỉ nạp đơn khi admin bấm mở dòng — tránh N+1 request theo trang.
                    expandedRowRender: (r) => <RewardUserOrdersTable program={program} userId={r.userId} />,
                    rowExpandable: (r) => r.totalOrders > 0,
                }}
                pagination={{
                    current: query.page, pageSize: query.pageSize, total, showSizeChanger: true,
                    pageSizeOptions: [20, 50, 100, 200], showTotal: (t) => `${fmtNumber(t)} người`,
                    onChange: (page, pageSize) => setQuery((q) => ({ ...q, page, pageSize })),
                }}
            />
        </div>
    );
}

export default RewardParticipantsTab;
